import {
  CartesianGrid,
  Line,
  LineChart as RechartsLineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getSequentialColor, useChartColors } from "./chart-theme";

export interface LineSeriesConfig {
  /** Data key for this series */
  dataKey: string;
  /** Optional custom color (defaults to sequential palette) */
  color?: string;
  /** Optional stroke dash array for dashed lines */
  strokeDasharray?: string;
}

export interface LineChartProps {
  /** Chart data array */
  data: Array<Record<string, unknown>>;
  /** Key for X-axis values */
  xKey: string;
  /** Array of line series configurations */
  series: LineSeriesConfig[];
  /** Chart height in pixels */
  height?: number;
  /** Optional reference Y value (e.g., threshold) */
  referenceY?: number;
}

/**
 * Themed Recharts LineChart component.
 * Supports multi-series with sequential colors and optional reference line.
 */
export function LineChart({ data, xKey, series, height = 300, referenceY }: LineChartProps) {
  const colors = useChartColors();
  const theme =
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-theme") === "dark"
      ? "dark"
      : "light";

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsLineChart data={data} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
        <XAxis dataKey={xKey} stroke={colors.axis} tick={{ fill: colors.text, fontSize: 12 }} />
        <YAxis stroke={colors.axis} tick={{ fill: colors.text, fontSize: 12 }} />
        <Tooltip
          contentStyle={{
            backgroundColor: colors.surface,
            border: `1px solid ${colors.grid}`,
            borderRadius: "8px",
            color: colors.text,
          }}
        />
        {referenceY !== undefined && (
          <ReferenceLine
            y={referenceY}
            stroke={colors.negative}
            strokeDasharray="5 5"
            strokeWidth={2}
          />
        )}
        {series.map((s, index) => (
          <Line
            key={s.dataKey}
            type="monotone"
            dataKey={s.dataKey}
            stroke={s.color || getSequentialColor(index, theme)}
            strokeWidth={2}
            strokeDasharray={s.strokeDasharray}
            dot={{ fill: s.color || getSequentialColor(index, theme), r: 3 }}
            activeDot={{ r: 5 }}
          />
        ))}
      </RechartsLineChart>
    </ResponsiveContainer>
  );
}
